import { useRouter } from "next/router";

import { NoIndex } from "@/common/components/no-index";
import type { PageInfo } from "@/features/search/api/types";
import {
  type PageRuleTerms,
  isBelowPublishFloor,
  isUnlistedFacetPage,
} from "@/features/search/utils/index-policy.utils";
import {
  hasNonPaginationParams,
  isEmptyListing,
} from "@/features/search/utils/robots.utils";

type SearchRobotsProps = {
  /** The server's result count; `null` when the search failed and nothing is known. */
  pageInfo?: Pick<PageInfo, "total"> | null;
  /** The term rules the page ran with (`brand`, `category`, `material`, ...); `null` when unknown. */
  pageRules?: PageRuleTerms | null;
  /** The page's own service path (`furniture/sofas/leather`); `null` for a query search. */
  pageUrl?: string | null;
  /** WHO-6325: the server's thin-page verdict; `false` while the gate is off. */
  thinListing?: boolean;
};

/**
 * `noindex,follow` on a listing or search URL whose query string holds anything but `page`, on a
 * listing the service found nothing live for (see `isEmptyListing`), on a brand x category page
 * under the publish floor and on a material or colour page outside the facet whitelist (see
 * `index-policy.utils.ts`), and on a page the server judged thin (see `thin-page.utils.ts`).
 *
 * Rendered from `SearchPage` rather than `SearchSeo` because the latter renders nothing for a
 * plain query search, and `/search?query=...&sort=...` needs the directive just as much as a
 * landing page with a facet applied. The tag shares its `key` with the page-level `NoIndex`, so
 * a landing page the service already marks `noIndex` still ends up with one robots tag.
 */
export const SearchRobots = ({
  pageInfo,
  pageRules,
  pageUrl,
  thinListing = false,
}: SearchRobotsProps) => {
  const { query } = useRouter();

  const indexable =
    !hasNonPaginationParams(query) &&
    !isEmptyListing(pageInfo) &&
    !isBelowPublishFloor(pageRules, pageInfo) &&
    !isUnlistedFacetPage(pageRules, pageUrl) &&
    !thinListing;

  if (indexable) {
    return null;
  }

  return <NoIndex content="noindex,follow" />;
};
